// src/components/reports/DateRangePicker.tsx
import React from 'react';

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  label?: string;
  className?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  label = "Date Range",
  className = "",
}) => {
  // Set default to current calendar year
  React.useEffect(() => {
    if (!startDate || !endDate) {
      const currentYear = new Date().getFullYear();
      const defaultStart = `${currentYear}-01-01`;
      const defaultEnd = `${currentYear}-12-31`;
      
      if (!startDate) onStartDateChange(defaultStart);
      if (!endDate) onEndDateChange(defaultEnd);
    }
  }, [startDate, endDate, onStartDateChange, onEndDateChange]);

  const setCurrentYear = () => {
    const currentYear = new Date().getFullYear();
    onStartDateChange(`${currentYear}-01-01`);
    onEndDateChange(`${currentYear}-12-31`);
  };

  const setPreviousYear = () => {
    const previousYear = new Date().getFullYear() - 1;
    onStartDateChange(`${previousYear}-01-01`);
    onEndDateChange(`${previousYear}-12-31`);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      {/* Quick Select Buttons */}
      <div className="flex space-x-2">
        <button
          type="button"
          onClick={setCurrentYear}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Current Year
        </button>
        <button
          type="button"
          onClick={setPreviousYear}
          className="px-3 py-1 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Previous Year
        </button>
      </div>
      
      {/* Date Inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
        
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
